import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ComplianceAlertBanner from '../../components/ui/ComplianceAlertBanner';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import UserContextSwitcher from '../../components/ui/UserContextSwitcher';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';


// Import all components
import ReportTypeSelector from './components/ReportTypeSelector';
import ReportConfiguration from './components/ReportConfiguration';
import ReportPreview from './components/ReportPreview';
import AuditTrail from './components/AuditTrail';
import ReportStatusTracker from './components/ReportStatusTracker';

const ComplianceReports = () => {
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportConfig, setReportConfig] = useState({
    vessels: [],
    reportingPeriod: '',
    jurisdiction: '',
    format: 'pdf',
    startDate: '2024-01-01',
    endDate: '2024-12-31'
  });
  const [isReportGenerated, setIsReportGenerated] = useState(false);

  const tabs = [
    { id: 'generate', label: 'Generate Reports', icon: 'FileText', count: null },
    { id: 'status', label: 'Status Tracker', icon: 'Activity', count: 4 },
    { id: 'audit', label: 'Audit Trail', icon: 'Shield', count: null }
  ];

  const handleReportTypeSelect = (type) => {
    setSelectedReportType(type);
  };

  const handleConfigChange = (config) => {
    setReportConfig(config);
  };

  const handleReportGenerate = () => {
    setIsReportGenerated(true);
  };

  const handleReportExport = () => {
    console.log('Exporting report...', { type: selectedReportType, config: reportConfig });
    // Simulate export process
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'generate':
        return (
          <div className="space-y-8">
            {/* Report Type Selection */}
            <ReportTypeSelector
              selectedType={selectedReportType}
              onTypeSelect={handleReportTypeSelect}
            />
            {/* Report Configuration */}
            {selectedReportType && (
              <ReportConfiguration
                reportType={selectedReportType}
                config={reportConfig}
                onConfigChange={handleConfigChange}
              />
            )}
            {/* Report Preview */}
            {selectedReportType && reportConfig?.vessels?.length > 0 && (
              <ReportPreview
                reportType={selectedReportType}
                config={reportConfig}
                onGenerate={handleReportGenerate}
                onExport={handleReportExport}
              />
            )}
          </div>
        );

      case 'status':
        return <ReportStatusTracker />;

      case 'audit':
        return <AuditTrail />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Compliance Reports - Carbon Cost Router</title>
        <meta name="description" content="Generate audit-ready compliance documentation for EU ETS, FuelEU, and IMO regulations with one-click report creation and immutable audit trails." />
      </Helmet>
      <Header />
      <ComplianceAlertBanner />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={20} strokeWidth={2} color="white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Compliance Reports</h1>
                  <p className="text-muted-foreground">
                    Generate audit-ready documentation for regulatory compliance
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4 mt-4 lg:mt-0">
              <SystemStatusIndicator />
              <UserContextSwitcher />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="maritime-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-foreground">4</div>
                  <div className="text-sm text-muted-foreground">Active Reports</div>
                </div>
                <Icon name="FileText" size={24} strokeWidth={2} className="text-primary" />
              </div>
            </div>

            <div className="maritime-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-success">1</div>
                  <div className="text-sm text-muted-foreground">Acknowledged</div>
                </div>
                <Icon name="CheckCircle" size={24} strokeWidth={2} className="text-success" />
              </div>
            </div>

            <div className="maritime-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-warning">2</div>
                  <div className="text-sm text-muted-foreground">Pending Review</div>
                </div>
                <Icon name="Clock" size={24} strokeWidth={2} className="text-warning" />
              </div>
            </div>

            <div className="maritime-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-error">45</div>
                  <div className="text-sm text-muted-foreground">Days to Deadline</div>
                </div>
                <Icon name="AlertTriangle" size={24} strokeWidth={2} className="text-error" />
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="maritime-card mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-6">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-maritime ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} strokeWidth={2} />
                    <span>{tab?.label}</span>
                    {tab?.count && (
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-medium">
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
      <QuickActionToolbar />
    </div>
  );
};

export default ComplianceReports;